import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote } from 'lucide-react';
import { registrations } from '../data/mock';
import { ParticlesBg } from '../components/layout/ParticlesBg';
import { Section, SectionHeading, Eyebrow, PageHero, Ridgeline, fadeInUp } from '../components/layout/Section';
import { SpotlightCard, WindowFrame, Reveal } from '../components/layout/Tech';
import { Globe3D, ScrollTilt } from '../components/layout/Motion3D';

const registrationAccents = ['accent-green', 'accent-orange', 'accent-blue', 'accent-purple', 'accent-pink'];

/** Abstract "mountains to the world" illustration drawn in code. */
const JourneyGraphic = () => (
  <svg viewBox="0 0 800 300" className="w-full h-auto" role="img" aria-label="Abstract illustration of Himalayan ridgelines connecting to a global network">
    <defs>
      <linearGradient id="jg-sky" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#0B1630" />
        <stop offset="1" stopColor="#15123A" />
      </linearGradient>
      <linearGradient id="jg-peak" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="rgba(34,211,238,0.55)" />
        <stop offset="1" stopColor="rgba(34,211,238,0.05)" />
      </linearGradient>
      <linearGradient id="jg-peak2" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="rgba(167,139,250,0.5)" />
        <stop offset="1" stopColor="rgba(167,139,250,0.04)" />
      </linearGradient>
      <radialGradient id="jg-glow" cx="0.78" cy="0.3" r="0.5">
        <stop offset="0" stopColor="rgba(52,211,153,0.5)" />
        <stop offset="1" stopColor="rgba(52,211,153,0)" />
      </radialGradient>
    </defs>
    <rect width="800" height="300" rx="20" fill="url(#jg-sky)" />
    <rect width="800" height="300" rx="20" fill="url(#jg-glow)" />
    {/* grid */}
    {Array.from({ length: 15 }).map((_, i) => (
      <line key={`v${i}`} x1={i * 57} x2={i * 57} y1="0" y2="300" stroke="rgba(255,255,255,0.04)" />
    ))}
    {Array.from({ length: 6 }).map((_, i) => (
      <line key={`h${i}`} x1="0" x2="800" y1={i * 60} y2={i * 60} stroke="rgba(255,255,255,0.04)" />
    ))}
    {/* globe */}
    <circle cx="640" cy="110" r="70" fill="none" stroke="rgba(52,211,153,0.6)" strokeWidth="1.2" />
    <ellipse cx="640" cy="110" rx="30" ry="70" fill="none" stroke="rgba(52,211,153,0.4)" strokeWidth="1" />
    <line x1="570" y1="110" x2="710" y2="110" stroke="rgba(52,211,153,0.4)" />
    <line x1="580" y1="75" x2="700" y2="75" stroke="rgba(52,211,153,0.3)" />
    <line x1="580" y1="145" x2="700" y2="145" stroke="rgba(52,211,153,0.3)" />
    {/* network nodes */}
    {[[380, 120], [450, 80], [520, 150], [600, 60], [690, 150], [560, 100]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3.5" fill="#67E8F9" />
    ))}
    <polyline points="380,120 450,80 520,150 560,100 600,60 690,150" fill="none" stroke="rgba(103,232,249,0.6)" strokeWidth="1.2" strokeDasharray="4 4" />
    {/* ridgelines */}
    <path d="M0 230 L70 150 L130 200 L200 90 L270 170 L340 120 L400 190 L470 160 L540 220 L800 230 L800 300 L0 300 Z" fill="url(#jg-peak2)" />
    <path d="M0 265 L60 215 L120 240 L190 170 L260 230 L330 195 L400 245 L480 215 L560 260 L800 265 L800 300 L0 300 Z" fill="url(#jg-peak)" />
    <polyline points="0,230 70,150 130,200 200,90 270,170 340,120 400,190 470,160 540,220" fill="none" stroke="rgba(196,181,253,0.8)" strokeWidth="1.4" />
    <polyline points="0,265 60,215 120,240 190,170 260,230 330,195 400,245 480,215 560,260" fill="none" stroke="rgba(103,232,249,0.9)" strokeWidth="1.4" />
    {/* snow caps */}
    <circle cx="200" cy="90" r="4" fill="#FFFFFF" />
    <circle cx="340" cy="120" r="3" fill="#FFFFFF" />
    <text x="24" y="40" fill="rgba(255,255,255,0.5)" fontFamily="JetBrains Mono, monospace" fontSize="11" letterSpacing="2">HIMACHAL PRADESH · 32.0°N 76.6°E</text>
    <text x="24" y="60" fill="rgba(103,232,249,0.85)" fontFamily="JetBrains Mono, monospace" fontSize="11" letterSpacing="2">→ GLOBAL DELIVERY</text>
  </svg>
);

export default function About() {
  return (
    <main>
      {/* Hero */}
      <PageHero
        tone="plum"
        eyebrow="Our Story"
        title="About Globistaan"
        subtitle="An IT services startup born in the serene mountains of Himachal Pradesh, building AI solutions for the global enterprise."
        glows={[
          { color: 'rgba(167,139,250,0.3)', top: '-10%', left: '-6%', size: 560 },
          { color: 'rgba(251,113,133,0.18)', bottom: '-20%', right: '-6%', size: 520 }
        ]}
        ridge
        decor={<Globe3D size={560} className="hero-globe" />}
      />

      {/* Story */}
      <Section tone="sky" pattern="grid" glows={[{ color: 'rgba(96,165,250,0.35)', top: '-10%', left: '-10%', size: 520 }]} contentWidth={1000}>
        <motion.div {...fadeInUp} className="content-glass max-w-[820px] mx-auto">
          <h2 className="h-section mb-8 text-center" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)' }}>
            From the Mountains to the World
          </h2>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <ScrollTilt>
              <WindowFrame url="baijnath.himachal → world" className="w-full">
                <JourneyGraphic />
              </WindowFrame>
            </ScrollTilt>
          </motion.div>

          <div className="flex flex-col gap-6" style={{ fontSize: '1rem', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
            <p>
              The idea of Globistaan came into existence as we want to be a valuable part of the global community and see tremendous scope in the technology sector as a software development services company. This venture is a humble attempt in solving challenges and exploring new avenues in the field of technology.
            </p>
            <p>
              We are a small and lean IT company, open to clients of all sizes and needs, and we are easy to adapt. Whether you are a startup or a large bank that needs talented programmers to build and support your applications — we onboard resources based on client requirement, and we are very fast and careful to match your needs.
            </p>
            <p>
              Today, we're evolving beyond traditional IT services into the AI frontier — building intelligent products that leverage practical automation, natural language interfaces, and generative AI to solve real-world problems.
            </p>
          </div>
        </motion.div>
      </Section>

      {/* Founder Quote */}
      <Section
        tone="teal"
        pattern="hatch"
        aurora
        topo
        glows={[
          { color: 'rgba(52,211,153,0.22)', top: '-20%', right: '-5%', size: 560 },
          { color: 'rgba(34,211,238,0.16)', bottom: '-30%', left: '-5%', size: 560 }
        ]}
        contentWidth={800}
        padding="py-28"
      >
        <motion.div {...fadeInUp} className="text-center">
          <div className="icon-well lg mx-auto mb-7" style={{ '--card-accent': '#6EE7B7' }}><Quote className="w-6 h-6" /></div>
          <p className="italic mb-8" style={{ fontSize: 'clamp(1.15rem, 2.4vw, 1.5rem)', lineHeight: 1.75, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontWeight: 500 }}>
            "In the shadow of ancient Himalayas, where mist-kissed pines whisper secrets to the wind, we stand together — like roots weaving through fertile earth. Let's build together: not just code, but futures. From quiet valleys to soaring peaks, every line we write carves a path upward. Your vision. My craft. Our summit."
          </p>
          <div>
            <p style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>Bejoy K Pramanick</p>
            <p className="kicker mt-1" style={{ color: '#6EE7B7' }}>Founder & CEO</p>
          </div>
        </motion.div>
      </Section>

      {/* Mission & Vision */}
      <Section tone="mint" pattern="dots" glows={[{ color: 'rgba(52,211,153,0.35)', top: '-20%', right: '-8%', size: 520 }]} contentWidth={1100}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal className="h-full"><SpotlightCard accent="accent-purple" className="h-full" style={{ '--card-pad': '2.75rem' }}>
            <div className="mb-5"><Eyebrow>Mission</Eyebrow></div>
            <h3 className="mb-4" style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Democratize AI for Every Enterprise
            </h3>
            <p style={{ fontSize: '0.98rem', lineHeight: 1.7, color: 'var(--text-secondary)' }}>
              To make cutting-edge artificial intelligence accessible to businesses of all sizes, enabling them to automate, innovate, and scale with intelligent technology solutions built on trust and transparency.
            </p>
          </SpotlightCard></Reveal>
          <Reveal delay={0.1} className="h-full"><SpotlightCard accent="accent-cyan" className="h-full" style={{ '--card-pad': '2.75rem' }}>
            <div className="mb-5"><Eyebrow>Vision</Eyebrow></div>
            <h3 className="mb-4" style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Global AI Innovation Hub
            </h3>
            <p style={{ fontSize: '0.98rem', lineHeight: 1.7, color: 'var(--text-secondary)' }}>
              To become a globally recognized AI services company from India, known for building intelligent products that transform industries — from healthcare and education to commerce and enterprise operations.
            </p>
          </SpotlightCard></Reveal>
        </div>
      </Section>

      {/* Registrations */}
      <Section
        tone="slate"
        pattern="grid"
        glows={[
          { color: 'rgba(251,191,36,0.16)', top: '-20%', left: '-6%', size: 520 },
          { color: 'rgba(34,211,238,0.18)', bottom: '-20%', right: '-6%', size: 560 }
        ]}
        contentWidth={1100}
      >
        <SectionHeading
          eyebrow="Trust & Compliance"
          title="Government Registered & Compliant"
          className="mb-14"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {registrations.map((reg, i) => {
            const Icon = reg.icon;
            return (
              <Reveal key={reg.title} delay={i * 0.07} className="h-full">
              <SpotlightCard accent={registrationAccents[i % registrationAccents.length]} className="h-full" style={{ '--card-pad': '1.75rem' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="icon-well sm"><Icon className="w-5 h-5" /></div>
                  <span className="status-pill" style={{ '--status': '#34D399' }}><i className="status-dot" />Verified</span>
                </div>
                <h4 style={{ fontSize: '1.02rem', fontWeight: 700, marginBottom: '0.4rem', color: 'var(--text-primary)' }}>
                  {reg.title}
                </h4>
                <p style={{ fontSize: '0.88rem', lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                  {reg.description}
                </p>
              </SpotlightCard>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* CTA */}
      <Section
        tone="navy"
        pattern="none"
        rule={false}
        glows={[
          { color: 'rgba(34,211,238,0.28)', top: '-30%', left: '20%', size: 640 },
          { color: 'rgba(167,139,250,0.24)', bottom: '-30%', right: '10%', size: 560 }
        ]}
        contentWidth={700}
        padding="py-28"
      >
        <ParticlesBg />
        <motion.div {...fadeInUp} className="text-center relative z-10">
          <h2 className="h-section mb-5" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)' }}>
            Let's Build Together
          </h2>
          <p className="lede mx-auto mb-10">
            Explore our projects, meet our team, or start a conversation about how AI can transform your business.
          </p>
          <Link to="/contact" className="btn-primary btn-accent" style={{ padding: '1rem 2.5rem', fontSize: '0.85rem' }}>
            Get in Touch <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <Ridgeline opacity={0.7} height={140} />
      </Section>
    </main>
  );
}
